import type {
	PricingCampaignTemplateFormData,
	ScenarioTemplateFormData,
	SettingsTemplateFormDataGen,
} from '@/entities/settings-template'
import type { TreeNodeType } from '@/shared/lib/tree'
import type { FieldErrors } from 'react-hook-form'

import React, { Suspense, useMemo } from 'react'
import { formatDistanceToNow } from 'date-fns'
import { any, pipe, prop, propOr } from 'lodash/fp'
import { AlertTriangleIcon, InfoIcon } from 'lucide-react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
	createChoices,
	isFilterbaleField,
	isPreproField,
	toAdaptedSchema,
	useColumnsSchemaQuery,
} from '@/entities/column'
import {
	useBrandsQuery,
	useCategoriesQuery,
	useSettingsSchemasQuery,
} from '@/entities/core'
import { getPricingCampaignEngines } from '@/entities/pricing-campaign'
import { UsernameById } from '@/entities/user'

import { ActivityFields } from '@/widgets/pricing-campaign/activity'
import { AnchoringRulesFiledController } from '@/widgets/pricing-campaign/anchoring'
import { AssignmentFieldsController } from '@/widgets/pricing-campaign/assignment'
import { MinimalPriceStepFiledController } from '@/widgets/pricing-campaign/minimal-steps'
import { PriceLimitsFieldController } from '@/widgets/pricing-campaign/price-limits'
import {
	MarkdownPeriodsFieldController,
	PricingTacticRuleFieldController,
	PromoPeriodFieldController,
	TargetPriceTypeController,
} from '@/widgets/pricing-campaign/pricing-tactics'
import { RoundingRulesFiledController } from '@/widgets/pricing-campaign/rounding-rules'

import { ErrorBoundary } from '@/shared/components/ErrorBoundary'
import {
	RHFRadioGroup,
	RHFSelect,
	RHFTextField,
} from '@/shared/components/HookForm'
import RHFSettingsWidget from '@/shared/components/HookForm/RHFSettingsWidget'
import RHFStoplistCustomRules from '@/shared/components/HookForm/RHFStoplistCustomRules'
import {
	Alert,
	AlertDescription,
	AlertTitle,
} from '@/shared/components/ui/alert'
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from '@/shared/components/ui/card'
import {
	DescriptionDetail,
	DescriptionGroup,
	DescriptionList,
	DescriptionTerm,
} from '@/shared/components/ui/description-list'
import { Loader } from '@/shared/components/ui/loader'
import { ModalBody } from '@/shared/components/ui/modal'
import {
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from '@/shared/components/ui/tabs'
import {
	HiddenOnContext,
	PricingEngineType,
	SettingsTemplateType,
} from '@/shared/generated'
import {
	getEnumOptions,
	getResolvedSchema,
	translateOptions,
	translateSchemaKeys,
} from '@/shared/tools/json-schema-utils'

import { useSettingsTemplateContext } from './SettingsTemplateProvider'
import { SettingTabActions } from './SettingTabActions'

export default function SettingsTemplateResolver() {
	const { template_type } = useSettingsTemplateContext()
	switch (template_type) {
		case SettingsTemplateType.PricingCampaign:
			return <SettingsTemplatePricingCampaign />
		case SettingsTemplateType.Scenario:
			return <SettingsTemplateScenario />
		case SettingsTemplateType.ScenarioStopList:
			return (
				<>
					<SettinsgTemplateFormHeader />
					<ModalBody className='overflow-y-auto'>
						<ScenarioStopListSettings settingsPathPrefix='body' />
					</ModalBody>
				</>
			)
		case SettingsTemplateType.ScenarioBusinessConstraints:
			return (
				<>
					<SettinsgTemplateFormHeader />
					<ModalBody className='overflow-y-auto'>
						<ScenarioBusinessConstraintsSettings settingsPathPrefix='body' />
					</ModalBody>
				</>
			)
		case SettingsTemplateType.ScenarioStrategy:
			return (
				<>
					<SettinsgTemplateFormHeader />
					<ModalBody className='overflow-y-auto'>
						<ScenarioStrategySettings settingsPathPrefix='body' />
					</ModalBody>
				</>
			)
		case SettingsTemplateType.PricingCampaignProductAssignment:
			return (
				<>
					<SettinsgTemplateFormHeader />
					<ModalBody className='overflow-y-auto'>
						<PricingCampaignAssignmentSettings settingsPathPrefix='body' />
					</ModalBody>
				</>
			)
		case SettingsTemplateType.PricingCampaignLimits:
			return (
				<>
					<SettinsgTemplateFormHeader />
					<ModalBody className='overflow-y-auto'>
						<PricingCampaignRepricingLimitsSettings settingsPathPrefix='body' />
					</ModalBody>
				</>
			)
		case SettingsTemplateType.PricingCampaignMinimalSteps:
			return (
				<>
					<SettinsgTemplateFormHeader />
					<ModalBody className='overflow-y-auto'>
						<PricingCampaignMinimalPriceStepSettings settingsPathPrefix='body' />
					</ModalBody>
				</>
			)
		case SettingsTemplateType.PricingCampaignRounding:
			return (
				<>
					<SettinsgTemplateFormHeader />
					<ModalBody className='overflow-y-auto'>
						<PricingCampaignRoundingRulesSettings settingsPathPrefix='body' />
					</ModalBody>
				</>
			)
		case SettingsTemplateType.PricingCampaignAnchoring:
			return (
				<>
					<SettinsgTemplateFormHeader />
					<ModalBody className='overflow-y-auto'>
						<PricingCampaignAnchoringSettings settingsPathPrefix='body' />
					</ModalBody>
				</>
			)
		case SettingsTemplateType.PricingCampaignActivity:
			return (
				<>
					<SettinsgTemplateFormHeader />
					<ModalBody className='overflow-y-auto'>
						<PricingCampaignActivitySettings settingsPathPrefix='body' />
					</ModalBody>
				</>
			)
		case SettingsTemplateType.PricingCampaignTacticsMl:
		case SettingsTemplateType.PricingCampaignTacticsRb:
		case SettingsTemplateType.PricingCampaignTacticsMd:
		case SettingsTemplateType.PricingCampaignTacticsPromo:
			return (
				<>
					<SettinsgTemplateFormHeader />
					<ModalBody className='overflow-y-auto'>
						<PricingCampaignTacticsSettings settingsPathPrefix='body' />
					</ModalBody>
				</>
			)
		default:
			return <div>Not implemented</div>
	}
}
function SettinsgTemplateFormHeader() {
	const { t } = useTranslation()

	const { template, template_type } = useSettingsTemplateContext()

	return (
		<div className='my-4'>
			<div className='relative flex items-center justify-between'>
				<RHFTextField
					className='w-96'
					name='name'
					autoComplete='off'
					label={t('template.name.label', { defaultValue: 'Template name' })}
					required
				/>
			</div>
			<DescriptionList className='mt-4 flex items-start gap-10'>
				<DescriptionGroup>
					<DescriptionTerm>
						{t('created_at', { defaultValue: 'Created at' })}
					</DescriptionTerm>
					<DescriptionDetail>
						{template?.created_at ? (
							<span className='font-medium'>
								{formatDistanceToNow(new Date(template?.created_at + 'Z'), {
									addSuffix: true,
								})}
								{template?.created_at &&
									template?.created_by &&
									` ${t('by', { defaultValue: 'by' })} `}
								{template?.created_by && (
									<UsernameById userId={template?.created_by} />
								)}
							</span>
						) : (
							'-'
						)}
					</DescriptionDetail>
				</DescriptionGroup>
				<DescriptionGroup>
					<DescriptionTerm>
						{t('last_modified_at', { defaultValue: 'Last modified' })}
					</DescriptionTerm>
					<DescriptionDetail>
						{template?.updated_at ? (
							<span className='font-medium'>
								{formatDistanceToNow(new Date(template?.updated_at + 'Z'), {
									addSuffix: true,
								})}
								{template?.updated_at &&
									template?.updated_by &&
									` ${t('by', { defaultValue: 'by' })} `}
								{template?.updated_by && (
									<UsernameById userId={template?.updated_by} />
								)}
							</span>
						) : (
							'-'
						)}
					</DescriptionDetail>
				</DescriptionGroup>
				<DescriptionGroup>
					<DescriptionTerm>
						{t('template_type', { defaultValue: 'Template type' })}
					</DescriptionTerm>
					<DescriptionDetail>
						<span className='font-medium'>
							{t(`template.type.${template_type}`)}
						</span>
					</DescriptionDetail>
				</DescriptionGroup>
			</DescriptionList>
		</div>
	)
}
function SettingsTemplateScenario() {
	const { t } = useTranslation()

	const { methods } = useSettingsTemplateContext()
	const tabs = useMemo(
		() => [
			{
				key: 'strategy',
				label: t('settings.strategy.short', { defaultValue: 'Strategy' }),
				hasError: (
					errors: FieldErrors<
						SettingsTemplateFormDataGen<ScenarioTemplateFormData>
					>
				) => errors?.body?.settings?.strategy,
				TabContent: ScenarioStrategySettings,
			},

			{
				key: 'business_constraints',
				label: t('settings.business.short', { defaultValue: 'Constrains' }),
				hasError: (
					errors: FieldErrors<
						SettingsTemplateFormDataGen<ScenarioTemplateFormData>
					>
				) => errors?.body?.settings?.business,
				TabContent: ScenarioBusinessConstraintsSettings,
			},
			{
				key: 'stop_list',
				label: t('settings.stop_list.short', { defaultValue: 'Stop list' }),
				hasError: (
					errors: FieldErrors<
						SettingsTemplateFormDataGen<ScenarioTemplateFormData>
					>
				) => errors?.body?.settings?.stop_list,
				TabContent: ScenarioStopListSettings,
			},
		],
		[]
	)

	return (
		<>
			<SettinsgTemplateFormHeader />
			<Tabs defaultValue='strategy' asChild>
				<ModalBody className='overflow-hidden'>
					<TabsList>
						{tabs.map((tab) => (
							<TabsTrigger
								data-testid='scenario-template-modal-tab'
								data-tab={tab.key}
								key={tab.key}
								value={tab.key}
							>
								{tab.label}
								{tab.hasError(methods.formState.errors) && (
									<AlertTriangleIcon className='text-destructive' />
								)}
							</TabsTrigger>
						))}
					</TabsList>
					{tabs.map(({ TabContent, key }) => (
						<TabsContent
							data-testid='scenario-modal-tab-content'
							className='-mx-4 flex flex-col overflow-y-auto px-4'
							value={key}
							key={key}
						>
							<TabContent settingsPathPrefix='body.settings' />
						</TabsContent>
					))}
				</ModalBody>
			</Tabs>
		</>
	)
}
function SettingsTemplatePricingCampaign() {
	const { t } = useTranslation()

	const { methods } = useSettingsTemplateContext()
	const tabs = useMemo(
		() =>
			[
				{
					key: 'activity',
					label: t('settings.activity.short', { defaultValue: 'Activity' }),
					hasError: (
						errors: FieldErrors<
							SettingsTemplateFormDataGen<PricingCampaignTemplateFormData>
						>
					) => errors?.body?.settings?.activity,
					TabContent: PricingCampaignActivitySettings,
				},
				{
					key: 'assignment',
					label: t('settings.assignment.short', { defaultValue: 'Assignment' }),
					hasError: (
						errors: FieldErrors<
							SettingsTemplateFormDataGen<PricingCampaignTemplateFormData>
						>
					) => errors?.body?.settings?.product_assignments,
					TabContent: PricingCampaignAssignmentSettings,
				},
				{
					key: 'tactics',
					label: t('settings.tactics.short', { defaultValue: 'Tactics' }),
					hasError: (
						errors: FieldErrors<
							SettingsTemplateFormDataGen<PricingCampaignTemplateFormData>
						>
					) => errors?.name || errors?.body?.settings?.pricing_tactics,
					TabContent: () => {
						const { t } = useTranslation()

						const { isNew } = useSettingsTemplateContext()

						return (
							<>
								{isNew && (
									<Card
										className='mb-3'
										data-testid='settings-template-tactics-panel'
									>
										<CardContent>
											<RHFRadioGroup
												label={t('engine', { defaultValue: 'Engine' })}
												name='body.settings.pricing_tactics.engine'
												options={getPricingCampaignEngines(t)}
											/>
											<Alert variant='warning' className='mt-2'>
												<InfoIcon />
												<AlertTitle>
													{t('pc.tactics.disabled_warning', {
														defaultValue:
															'You can`t change pricing tactics engine after template creation',
													})}
												</AlertTitle>
											</Alert>
										</CardContent>
									</Card>
								)}
								<PricingCampaignTacticsSettings settingsPathPrefix='body.settings'>
									<SettingTabActions setting='tactics' />
								</PricingCampaignTacticsSettings>
							</>
						)
					},
				},

				{
					key: 'repricing_limits',
					label: t('settings.price_limits.short', { defaultValue: 'Limits' }),
					hasError: (
						errors: FieldErrors<
							SettingsTemplateFormDataGen<PricingCampaignTemplateFormData>
						>
					) => errors?.body?.settings?.price_limits,
					TabContent: PricingCampaignRepricingLimitsSettings,
				},
				{
					key: 'anchoring',
					label: t('settings.anchoring.short', { defaultValue: 'Anchoring' }),
					hasError: (
						errors: FieldErrors<
							SettingsTemplateFormDataGen<PricingCampaignTemplateFormData>
						>
					) => errors?.body?.settings?.anchoring,
					TabContent: PricingCampaignAnchoringSettings,
				},
				{
					key: 'rounding',
					label: t('settings.rounding.short', { defaultValue: 'Rounding' }),
					hasError: (
						errors: FieldErrors<
							SettingsTemplateFormDataGen<PricingCampaignTemplateFormData>
						>
					) => errors?.body?.settings?.rounding,
					TabContent: PricingCampaignRoundingRulesSettings,
				},
				{
					key: 'min_steps',
					label: t('settings.minimal_steps.short', {
						defaultValue: 'Minimal steps',
					}),
					hasError: (
						errors: FieldErrors<
							SettingsTemplateFormDataGen<PricingCampaignTemplateFormData>
						>
					) => errors?.body?.settings?.minimal_steps,
					TabContent: PricingCampaignMinimalPriceStepSettings,
				},
			].filter(Boolean),
		[]
	)

	return (
		<>
			<SettinsgTemplateFormHeader />
			<Tabs defaultValue='assignment' asChild>
				<ModalBody className='overflow-hidden'>
					<TabsList>
						{tabs.map((tab) => (
							<TabsTrigger
								data-testid='pc-template-modal-tab'
								data-tab={tab.key}
								key={tab.key}
								value={tab.key}
							>
								{tab.label}
								{tab.hasError(methods.formState.errors) && (
									<AlertTriangleIcon className='text-destructive' />
								)}
							</TabsTrigger>
						))}
					</TabsList>
					{tabs.map(({ TabContent, key }) => (
						<TabsContent
							data-testid='pc-modal-tab-content'
							className='-mx-4 flex flex-col overflow-y-auto px-4'
							value={key}
							key={key}
						>
							<TabContent settingsPathPrefix='body.settings'>
								<SettingTabActions setting={key} />
							</TabContent>
						</TabsContent>
					))}
				</ModalBody>
			</Tabs>
		</>
	)
}

function ScenarioStrategySettings({
	settingsPathPrefix,
}: {
	settingsPathPrefix: string
}) {
	const { t } = useTranslation()

	const schemaQuery = useSettingsSchemasQuery({
		select: pipe([
			getResolvedSchema('part:strategy'),
			(schema) => translateSchemaKeys(schema, t),
		]),
	})
	if (schemaQuery.isLoading) {
		return (
			<div className='flex min-h-96 w-full items-center justify-center'>
				<Loader />
			</div>
		)
	}

	return (
		<Card data-testid='settings-template-strategies-panel'>
			<CardHeader>
				<CardTitle>
					{t('settings.strategy.long', { defaultValue: 'Pricing strategy' })}
				</CardTitle>
			</CardHeader>
			<CardContent>
				<ErrorBoundary>
					<RHFSelect
						name={`${settingsPathPrefix}.strategy.demand_strategy.target`}
						className='w-56'
						label={prop(
							'properties.demand_strategy.properties.target.title',
							schemaQuery.data
						)}
						description={prop(
							'properties.demand_strategy.properties.target.description',
							schemaQuery.data
						)}
						options={translateOptions(
							getEnumOptions(
								prop(
									'properties.demand_strategy.properties.target',
									schemaQuery.data
								)
							),
							t
						)}
					/>
					<RHFSelect
						name={`${settingsPathPrefix}.strategy.demand_strategy.protect`}
						className='w-56'
						label={prop(
							'properties.demand_strategy.properties.protect.title',
							schemaQuery.data
						)}
						description={prop(
							'properties.demand_strategy.properties.protect.description',
							schemaQuery.data
						)}
						options={translateOptions(
							getEnumOptions(
								prop(
									'properties.demand_strategy.properties.protect',
									schemaQuery.data
								)
							),
							t
						)}
					/>
				</ErrorBoundary>
			</CardContent>
			<CardHeader>
				<CardTitle>
					{t('optimization_groups_settings_tab_markdown_title')}
				</CardTitle>
				<CardDescription>
					{t('optimization_groups_settings_tab_markdown_subtitle')}
				</CardDescription>
			</CardHeader>
			<CardContent>
				<ErrorBoundary>
					<RHFSelect
						name={`${settingsPathPrefix}.strategy.markdown_strategy.target`}
						className='w-56'
						label={prop(
							'properties.markdown_strategy.properties.target.title',
							schemaQuery.data
						)}
						description={prop(
							'properties.markdown_strategy.properties.target.description',
							schemaQuery.data
						)}
						options={translateOptions(
							getEnumOptions(
								prop(
									'properties.markdown_strategy.properties.target',
									schemaQuery.data
								)
							),
							t
						)}
					/>
				</ErrorBoundary>
			</CardContent>
		</Card>
	)
}
function ScenarioBusinessConstraintsSettings({
	settingsPathPrefix,
}: {
	settingsPathPrefix: string
}) {
	const { t } = useTranslation()

	const schemaQuery = useSettingsSchemasQuery({
		select: pipe([
			getResolvedSchema('part:business'),
			(schema) => translateSchemaKeys(schema, t),
		]),
	})
	if (schemaQuery.isLoading) {
		return (
			<div className='flex min-h-96 w-full items-center justify-center'>
				<Loader />
			</div>
		)
	}

	return (
		<Card data-testid='settings-template-business-panel'>
			<CardHeader>
				<CardTitle>
					{t('settings.business.long', { defaultValue: 'Business constrains' })}
				</CardTitle>
				<CardDescription>
					{t('optimization_groups_settings_business_subtitle')}
				</CardDescription>
			</CardHeader>
			<CardContent>
				<ErrorBoundary>
					<RHFSettingsWidget
						name={`${settingsPathPrefix}.business`}
						schema={schemaQuery.data}
					/>
				</ErrorBoundary>
			</CardContent>
		</Card>
	)
}
function ScenarioStopListSettings({
	settingsPathPrefix,
}: {
	settingsPathPrefix: string
}) {
	const { t } = useTranslation()

	const schemaQuery = useSettingsSchemasQuery({
		select: pipe([
			getResolvedSchema('part:stop_list'),
			(schema) => translateSchemaKeys(schema, t),
		]),
	})
	const fieldsQuery = useColumnsSchemaQuery(HiddenOnContext.Assortment, {
		select: (data) => data.filter(isPreproField).filter(isFilterbaleField),
	})
	const brandsQuery = useBrandsQuery({})
	const categoriesQuery = useCategoriesQuery<TreeNodeType[]>({})
	const fields = useMemo(
		() => (fieldsQuery.data || []).map((schema) => toAdaptedSchema(schema, t)),
		[fieldsQuery.data, t]
	)
	const dataChoices = useMemo(
		() =>
			createChoices({
				schema: fieldsQuery.data || [],
				entities: {
					brand: brandsQuery.data || [],
					category: categoriesQuery.data || [],
				},
			}),
		[brandsQuery.data, categoriesQuery.data, fieldsQuery.data]
	)
	const isLoading = any(prop('isLoading'), [
		fieldsQuery,
		brandsQuery,
		categoriesQuery,
	])
	if (isLoading) {
		return (
			<div className='flex min-h-96 w-full items-center justify-center'>
				<Loader />
			</div>
		)
	}

	return (
		<div className='flex flex-col gap-3'>
			<Card data-testid='settings-template-stop-list-panel'>
				<CardHeader>
					<CardTitle>{t('stop_list.default_rules.title')}</CardTitle>
					<CardDescription>
						{t('stop_list.default_rules.subtitle')}
					</CardDescription>
				</CardHeader>
				<CardContent>
					<ErrorBoundary>
						<RHFSettingsWidget
							name={`${settingsPathPrefix}.stop_list.default_rules`}
							schema={schemaQuery.data?.properties.default_rules}
						/>
					</ErrorBoundary>
				</CardContent>
				<CardHeader>
					<CardTitle>{t('stop_list.custom_rules.title')}</CardTitle>
					<CardDescription>
						{t('stop_list.custom_rules.subtitle')}
					</CardDescription>
				</CardHeader>
				<CardContent>
					<ErrorBoundary>
						<RHFStoplistCustomRules
							name={`${settingsPathPrefix}.stop_list.custom_rules`}
							fields={fields}
							dataChoices={dataChoices}
						/>
					</ErrorBoundary>
				</CardContent>
			</Card>
		</div>
	)
}
function PricingCampaignTacticsSettings({
	settingsPathPrefix,
	children,
}: {
	settingsPathPrefix: string
	children?: React.ReactNode
}) {
	const { t } = useTranslation()

	const { watch } = useFormContext()
	const { template_type } = useSettingsTemplateContext()
	const engine = watch(`${settingsPathPrefix}.pricing_tactics.engine`)
	const paramsPath = `${settingsPathPrefix}.pricing_tactics.params`

	return (
		<>
			{template_type === SettingsTemplateType.PricingCampaign && (
				<TargetPriceTypeController
					engine={engine}
					name='body.settings.pricing_tactics.target_price_type'
				/>
			)}
			<Card data-testid='settings-template-tactics-panel' className='mt-3'>
				<CardHeader className='flex items-center justify-between'>
					<CardTitle>
						{t('settings.tactics.long', { defaultValue: 'Pricing tactics' })}
					</CardTitle>
					{children}
				</CardHeader>
				<CardContent>
					{engine === PricingEngineType.MD && (
						<MarkdownPeriodsFieldController name={paramsPath} />
					)}
					{engine === PricingEngineType.RB && (
						<Suspense
							fallback={
								<div className='text-sm text-muted-foreground'>
									{t('loading')}
								</div>
							}
						>
							<PricingTacticRuleFieldController name={paramsPath} />
						</Suspense>
					)}
					{engine === PricingEngineType.ML && (
						<Alert variant='info'>
							<AlertTitle>
								{t('global.pc.settings.demand_strategy.message', {
									defaultValue:
										'Pricing strategy will be inherited from the Optimization group settings.',
								})}
							</AlertTitle>
						</Alert>
					)}
					{engine === PricingEngineType.Promo && (
						<PromoPeriodFieldController name={paramsPath} />
					)}
				</CardContent>
			</Card>
		</>
	)
}
function PricingCampaignRepricingLimitsSettings({
	settingsPathPrefix,
	children,
}: {
	settingsPathPrefix: string
	children?: React.ReactNode
}) {
	const { t } = useTranslation()

	const settingsSchema = useSettingsSchemasQuery({
		select: pipe([
			getResolvedSchema('part:price_limits'),
			(schema) => translateSchemaKeys(schema, t),
		]),
	})
	if (settingsSchema.isLoading) {
		return (
			<div className='flex min-h-96 w-full items-center justify-center'>
				<Loader />
			</div>
		)
	}
	const schema = settingsSchema.data

	return (
		<Card data-testid='settings-template-price-limits-panel'>
			<CardHeader className='flex items-center justify-between'>
				<CardTitle>
					{t('settings.price_limits.long', {
						defaultValue: 'Repricing limits',
					})}
				</CardTitle>
				{children}
			</CardHeader>
			<CardContent className='space-y-5'>
				<ErrorBoundary>
					<PriceLimitsFieldController
						name={`${settingsPathPrefix}.price_limits.lower`}
						schema={prop('properties.lower', schema)}
					/>
					<PriceLimitsFieldController
						name={`${settingsPathPrefix}.price_limits.upper`}
						schema={prop('properties.upper', schema)}
					/>
				</ErrorBoundary>
			</CardContent>
			<CardHeader>
				<CardTitle>{prop('properties.extra.title', schema)}</CardTitle>
				<CardDescription>
					{prop('properties.extra.description', schema)}
				</CardDescription>
			</CardHeader>
			<CardContent>
				<RHFSelect
					name={`${settingsPathPrefix}.price_limits.extra.on_conflict`}
					defaultValue={propOr(
						[],
						'properties.extra.properties.on_conflict.default',
						schema
					)}
					label={prop('properties.extra.properties.on_conflict.title', schema)}
					options={getEnumOptions(
						prop('properties.extra.properties.on_conflict', schema)
					)}
				/>
			</CardContent>
		</Card>
	)
}

function PricingCampaignAssignmentSettings({
	settingsPathPrefix,
	children,
}: {
	settingsPathPrefix: string
	children?: React.ReactNode
}) {
	const { t } = useTranslation()

	return (
		<Card>
			<CardHeader className='flex items-center justify-between'>
				<CardTitle>
					{t('settings.assignment.long', {
						defaultValue: 'Product assignment',
					})}
				</CardTitle>
				{children}
			</CardHeader>
			<CardContent>
				<AssignmentFieldsController
					name={`${settingsPathPrefix}.product_assignments`}
				/>
			</CardContent>
		</Card>
	)
}
function PricingCampaignActivitySettings({
	settingsPathPrefix,
	children,
}: {
	settingsPathPrefix: string
	children?: React.ReactNode
}) {
	const { t } = useTranslation()

	const { data: schema, isLoading } = useSettingsSchemasQuery({
		select: pipe([
			getResolvedSchema('part:activity'),
			(schema) => translateSchemaKeys(schema, t),
		]),
	})
	const paramsPath = `${settingsPathPrefix}.activity`
	if (isLoading) {
		return (
			<div className='flex min-h-96 w-full items-center justify-center'>
				<Loader />
			</div>
		)
	}

	return (
		<Card data-testid='pc-anchoring-panel'>
			<CardHeader className='flex items-center justify-between'>
				<CardTitle>
					{t('settings.activity.long', { defaultValue: 'Activity' })}
				</CardTitle>
				{children}
			</CardHeader>
			<CardContent className='grid gap-4'>
				<ActivityFields name={paramsPath} schema={schema} />
			</CardContent>
		</Card>
	)
}
function PricingCampaignAnchoringSettings({
	settingsPathPrefix,
	children,
}: {
	settingsPathPrefix: string
	children?: React.ReactNode
}) {
	const { t } = useTranslation()

	const {
		data: schema,
		isLoading,
		error,
	} = useSettingsSchemasQuery({
		select: pipe([
			getResolvedSchema('entity:pricing_campaign'),
			prop('properties.settings.properties.anchoring'),
			(schema) => translateSchemaKeys(schema, t),
		]),
	})
	if (isLoading) {
		return (
			<div className='flex min-h-96 w-full items-center justify-center'>
				<Loader />
			</div>
		)
	}
	if (error?.message) {
		return (
			<Alert variant='destructive'>
				<AlertTriangleIcon />
				<AlertDescription>{error?.message}</AlertDescription>
			</Alert>
		)
	}

	return (
		<Card data-testid='settings-template-anchoring-panel'>
			<CardHeader className='flex items-center justify-between'>
				<CardTitle>
					{t('settings.anchoring.long', {
						defaultValue: 'Anchoring price behavior',
					})}
				</CardTitle>
				{children}
			</CardHeader>
			<CardContent>
				<AnchoringRulesFiledController
					name={`${settingsPathPrefix}.anchoring`}
				/>
			</CardContent>
			<CardHeader>
				<CardTitle>
					{t('anchoring.section.additional_settings.title', {
						defaultValue: 'Additional settings',
					})}
				</CardTitle>
				<CardDescription>
					{t('anchoring.section.additional_settings.subtitle', {
						defaultValue: 'Define the prior limit parameter in special cases.',
					})}
				</CardDescription>
			</CardHeader>
			<CardContent className='grid gap-4'>
				<RHFSelect
					className='w-64'
					name={`${settingsPathPrefix}.anchoring.on_conflict`}
					defaultValue={propOr([], 'properties.on_conflict.default', schema)}
					label={prop('properties.on_conflict.title', schema)}
					options={getEnumOptions(prop('properties.on_conflict', schema))}
				/>
				<RHFSelect
					className='w-64'
					name={`${settingsPathPrefix}.anchoring.on_conflict_anchor_limits`}
					defaultValue={propOr(
						[],
						'properties.on_conflict_anchor_limits.default',
						schema
					)}
					label={prop('properties.on_conflict_anchor_limits.title', schema)}
					options={getEnumOptions(
						prop('properties.on_conflict_anchor_limits', schema)
					)}
				/>
				<RHFSelect
					className='w-64'
					name={`${settingsPathPrefix}.anchoring.on_anchor_locked`}
					defaultValue={propOr(
						[],
						'properties.on_anchor_locked.default',
						schema
					)}
					label={prop('properties.on_anchor_locked.title', schema)}
					options={getEnumOptions(prop('properties.on_anchor_locked', schema))}
				/>
				<RHFSelect
					className='w-64'
					name={`${settingsPathPrefix}.anchoring.rounding_limits_and_price`}
					defaultValue={propOr(
						[],
						'properties.rounding_limits_and_price.default',
						schema
					)}
					label={prop('properties.rounding_limits_and_price.title', schema)}
					options={getEnumOptions(
						prop('properties.rounding_limits_and_price', schema)
					)}
				/>
			</CardContent>
		</Card>
	)
}
function PricingCampaignRoundingRulesSettings({
	settingsPathPrefix,
	children,
}: {
	settingsPathPrefix: string
	children?: React.ReactNode
}) {
	const { t } = useTranslation()

	const settingsSchema = useSettingsSchemasQuery({
		select: pipe([
			getResolvedSchema('part:rounding'),
			(schema) => translateSchemaKeys(schema, t),
		]),
	})
	if (settingsSchema.isLoading) {
		return (
			<div className='flex min-h-96 w-full items-center justify-center'>
				<Loader />
			</div>
		)
	}
	const schema = settingsSchema.data

	return (
		<Card data-testid='settings-template-rounding-panel'>
			<CardHeader className='flex items-center justify-between'>
				<CardTitle>
					{t('settings.rounding.long', { defaultValue: 'Rounding rules' })}
				</CardTitle>
				{children}
			</CardHeader>
			<CardContent>
				<RoundingRulesFiledController
					name={`${settingsPathPrefix}.rounding`}
					schema={schema}
				/>
			</CardContent>
		</Card>
	)
}
function PricingCampaignMinimalPriceStepSettings({
	settingsPathPrefix,
	children,
}: {
	settingsPathPrefix: string
	children?: React.ReactNode
}) {
	const { t } = useTranslation()

	const settingsSchema = useSettingsSchemasQuery({
		select: pipe([
			getResolvedSchema('part:minimal_steps'),
			(schema) => translateSchemaKeys(schema, t),
		]),
	})
	if (settingsSchema.isLoading) {
		return (
			<div className='flex min-h-96 w-full items-center justify-center'>
				<Loader />
			</div>
		)
	}
	const schema = settingsSchema.data

	return (
		<Card data-testid='settings-template-minimal-steps-panel'>
			<CardHeader className='flex items-center justify-between'>
				<CardTitle>
					{t('settings.minimal_steps.long', {
						defaultValue: 'Minimal price change',
					})}
				</CardTitle>
				{children}
			</CardHeader>
			<CardContent>
				<MinimalPriceStepFiledController
					name={`${settingsPathPrefix}.minimal_steps`}
					schema={schema}
				/>
			</CardContent>
		</Card>
	)
}
